import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from 'react';
import {DialogTitle} from '@priority-software/priority-style-react';
import {DialogModal} from '../../../../_shared/componnets/Dialog/Dialog';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import texts, {getServerTexts} from '../../../../components/Common/SelectListModal/text';
import {SearchableList} from './SearchableList';
import {formHasActiveKeys, mapListDataByType} from './utils';
import {listTypes} from './enum';
import {keys} from '../../../../constants/KeyCodes';
import {useEventListener} from '../../../../hooks/useEventListener';
import {openNavigationModal} from '../../../../_shared/GWTBridge/invoke/genPage';

type Props = {
    isApiLock: () => boolean;
};

export interface SelectListModalRefType {
    openSelectListModal: (listType: listTypes) => void;
}

export const SelectListModal = forwardRef(({isApiLock}: Props, ref) => {
    const [listType, setListType] = useState<listTypes | ''>('');
    const [isOpen, setOpen] = useState(false);

    const onDialogClose = useCallback(() => {
        setOpen(false);
    }, []);

    const onDialogOpen = useCallback(
        (listType: listTypes) => {
            setOpen(true);
            setListType(listType);
        },
        [setOpen]
    );

    const handleDialog = useCallback(
        (e: KeyboardEvent) => {
            if (isOpen || isApiLock() || e.altKey) return;

            if (e.key === keys.F5 && !e.ctrlKey && formHasActiveKeys()) {
                onDialogOpen(listTypes.FormSons);
            } else if (e.key === keys.F5 && e.ctrlKey) {
                onDialogOpen(listTypes.DirectActivations);
            }
        },
        [onDialogOpen, isOpen, isApiLock]
    );

    const handleKeyUp = (e: KeyboardEvent) => {
        if (e.ctrlKey && (e.key === keys.ARROW_DOWN || e.key === keys.ARROW_UP)) {
            onDialogClose();
            openNavigationModal(e.key === keys.ARROW_DOWN);
        }
    };

    useEventListener('keyup', handleKeyUp);
    useEventListener('keydown', handleDialog);

    useImperativeHandle(
        ref,
        () => {
            return {
                openSelectListModal: onDialogOpen
            };
        },
        [onDialogOpen]
    );

    const renderContent = () => {
        if (!listType) return null;

        const items = mapListDataByType(listType);

        return (
            <SearchableList
                key={`${listType}-${isOpen}`}
                open={isOpen}
                items={items}
                listType={listType}
                onClose={onDialogClose}
                noResultMsg={texts[listType + 'NoResults']}
            />
        );
    };

    useEffect(() => {
        getServerTexts(); // Fetch server Texts
    }, []);

    if (isOpen) {
        return (
            <DialogModal
                isDialogOpen
                draggable
                content={renderContent()}
                onClose={onDialogClose}
                title={<DialogTitle title={texts[listType]} textColor="primary-700" />}
                width={624}
                size={'small'}
                zIndex={getTopDialogZindex()}
            />
        );
    }

    return null;
});
